import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Typography, Alert, TextField, IconButton, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useMonitoringReportShared } from '../../shared/context/MonitoringReportContext';
import ProjectPreviousFundingsTable from '../../../project/ProjectPreviousFundingsTable';
import { formatCurrency } from '../../../../common/utils/common.util';
import { ProjectPreviousFundingDTO } from '../../../project/project.type';
import { CommentsSection } from './CommentsSection';
import { getCommentsField } from './section-utils';
import { FieldType, SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { SectionCompleteToggle } from '../../shared/components/SectionCompleteToggle';
import { useCompletionToggleEffect } from '../../../../hooks/useCompletionToggleEffect';
import { BookmarkToggle } from './BookmarkToggle';
import { getNextActionItemOrder } from './action-items.util';

const NOTE_FIELD_NAME = 'Note';

const AUTO_COMMENT =
  'We have confirmed in our G702 Review Form that the amount shown received by the Contractor for the previous Application for Payment is the same amount we recommended funding in our previous report.';

export const DISCREPANCY_AUTO_COMMENT_INCREASE_PREFIX =
  'Please note that the amount shown received last period by the Contractor is not the same amount we recommended funding in our previous report.  Upon noticing the discrepancy, we contacted the lender to confirm whether an increase was made without our knowledge and find that the Lender did increase the funding amount as follows:';

export const DISCREPANCY_AUTO_COMMENT_REDUCTION_PREFIX =
  'Please note that the amount shown received last period by the Contractor is not the same amount we recommended funding in our previous report.  Upon noticing the discrepancy, we contacted the lender to confirm whether a reduction was made without our knowledge and find that the Lender did reduce the funding amount as follows:';

export const DISCREPANCY_AUTO_COMMENT_BORROWER_HOLDING_PREFIX =
  'Please note that the amount shown received last period by the Contractor is not the same amount we recommended funding in our previous report.  Upon noticing the discrepancy, we contacted the lender to confirm whether a reduction was made without our knowledge and find that the Lender funding amount and our recommendation were the same.  We understand the Borrower is still holding funds from the last lender draw which were not passed along to the Contractor.  The amount held is the difference of the following:';

export const DISCREPANCY_AUTO_COMMENT_BORROWER_USED_OUTSIDE_FUNDS_PREFIX =
  'Please note that the amount shown received last period by the Contractor is not the same amount we recommended funding in our previous report.  Upon noticing the discrepancy, we contacted the lender to confirm whether a reduction was made without our knowledge and find that the Lender funding amount and our recommendation were the same.  We understand the Borrower used funds outside the loan to pay the Contractor. The amount held is the difference of the following.';

/** Fallback for a mismatch with no signed adjustment row — doesn't assert increase or reduction. */
export const DISCREPANCY_AUTO_COMMENT_NEUTRAL_PREFIX =
  'Please note that the amount shown received last period by the Contractor is not the same amount we recommended funding in our previous report.  Review the difference, shown as follows:';

export type DiscrepancyCommentVariant = 'increase' | 'reduction' | 'borrowerHolding' | 'borrowerUsedOutsideFunds' | 'neutral';

const ALL_DISCREPANCY_COMMENT_VARIANTS: DiscrepancyCommentVariant[] = [
  'increase',
  'reduction',
  'borrowerHolding',
  'borrowerUsedOutsideFunds',
  'neutral',
];

const DISCREPANCY_COMMENT_PREFIXES: Record<DiscrepancyCommentVariant, string> = {
  increase: DISCREPANCY_AUTO_COMMENT_INCREASE_PREFIX,
  reduction: DISCREPANCY_AUTO_COMMENT_REDUCTION_PREFIX,
  borrowerHolding: DISCREPANCY_AUTO_COMMENT_BORROWER_HOLDING_PREFIX,
  borrowerUsedOutsideFunds: DISCREPANCY_AUTO_COMMENT_BORROWER_USED_OUTSIDE_FUNDS_PREFIX,
  neutral: DISCREPANCY_AUTO_COMMENT_NEUTRAL_PREFIX,
};

const DISCREPANCY_AUTO_COMMENT_DEFAULT_SUFFIX =
  'We have adjusted our G702 Review Form accordingly and our current recommendation for funding has taken this adjustment into account.';

const DISCREPANCY_AUTO_COMMENT_BORROWER_HOLDING_SUFFIX =
  "We have adjusted our recommendation amount by the difference and recommend that the Borrower use these funds, which they are currently holding, and the recommended funding amount to fund the Contractor for the entire amount of the Contractor's current application.";

const DISCREPANCY_COMMENT_SUFFIXES: Record<DiscrepancyCommentVariant, string> = {
  increase: DISCREPANCY_AUTO_COMMENT_DEFAULT_SUFFIX,
  reduction: DISCREPANCY_AUTO_COMMENT_DEFAULT_SUFFIX,
  borrowerHolding: DISCREPANCY_AUTO_COMMENT_BORROWER_HOLDING_SUFFIX,
  borrowerUsedOutsideFunds: DISCREPANCY_AUTO_COMMENT_DEFAULT_SUFFIX,
  neutral: DISCREPANCY_AUTO_COMMENT_DEFAULT_SUFFIX,
};

/** Resolved (Funded by Lender matches Received by Contractor) → reduction/increase; unresolved → borrowerHolding/borrowerUsedOutsideFunds. */
export const getDiscrepancyCommentVariant = (
  hasNegativeAdjustment: boolean,
  negativeAdjustmentResolved: boolean,
  hasPositiveAdjustment = false,
  positiveAdjustmentResolved = false,
): DiscrepancyCommentVariant => {
  if (hasNegativeAdjustment) return negativeAdjustmentResolved ? 'reduction' : 'borrowerHolding';
  if (hasPositiveAdjustment) return positiveAdjustmentResolved ? 'increase' : 'borrowerUsedOutsideFunds';
  return 'neutral';
};

export const buildDiscrepancyAutoComment = (
  recommended: number,
  received: number,
  variant: DiscrepancyCommentVariant = 'increase',
): string => {
  const prefix = DISCREPANCY_COMMENT_PREFIXES[variant];
  const suffix = DISCREPANCY_COMMENT_SUFFIXES[variant];
  return `${prefix}\n\nOur previous recommendation for funding: ${formatCurrency(recommended.toString())}\nThe amount shown received by the Contractor: ${formatCurrency(received.toString())}\n\n${suffix}`;
};

const PREVIOUS_FUNDINGS_SECTION_KEY = 'previous-fundings';

/** CDCC's recommended total — excludes adjustment rows, whose Funded by Lender only records how the discrepancy was resolved. */
const sumRecommendedFunding = (fundings?: ProjectPreviousFundingDTO[]): number =>
  (fundings || []).reduce((sum, funding) => (funding.isDiscrepancy ? sum : sum + (funding.amountFunded || 0)), 0);

const PreviousFundingsSection: React.FC = () => {
  const { reportState, setReportState, setIsDirty, isDirty, saveReport } = useMonitoringReportShared() as any;
  const { drawSummary } = reportState;

  const comment = reportState.previousFundingsComment || '';
  const sections = (reportState.sections || {}) as any;
  const previousFundingsSection = (sections['previous-fundings'] ||
    sections.previousFundings ||
    sections.previousFunding ||
    sections.previousFundingsSection) as SectionFieldCollection | undefined;
  const isCompleted = Boolean(previousFundingsSection?.isCompleted);

  // Derived in render (not copied into state by an effect) so both totals always come from the same
  // table snapshot — a one-render lag here left the comment built from a stale Received total.
  const previousFundingsTotal = useMemo(
    () =>
      (drawSummary?.previousFundings || []).reduce(
        (sum: number, funding: ProjectPreviousFundingDTO) => sum + (funding.receivedByContractor || 0),
        0,
      ),
    [drawSummary?.previousFundings],
  );
  const fundedByLenderTotal = useMemo(
    () =>
      (drawSummary?.previousFundings || []).reduce(
        (sum: number, funding: ProjectPreviousFundingDTO) => sum + (funding.amountFunded || 0),
        0,
      ),
    [drawSummary?.previousFundings],
  );
  const recommendedTotal = useMemo(() => sumRecommendedFunding(drawSummary?.previousFundings), [drawSummary?.previousFundings]);
  const [showMismatchAlert, setShowMismatchAlert] = useState(false);
  const [expanded, setExpanded] = useState(() => !isCompleted);
  const previousMatchState = useRef<boolean | null>(null);
  const previousDiscrepancyCommentVariant = useRef<DiscrepancyCommentVariant | null>(null);
  const savedCustomComment = useRef<string | null>(null);
  // The discrepancy auto-comment last written, so an unedited one can follow later table edits.
  const lastAutoComment = useRef<string | null>(null);
  const previousFundingsSectionCommentsField = (previousFundingsSection ? getCommentsField(previousFundingsSection) : undefined) || {
    fieldName: 'Comments',
    fieldType: FieldType.TextArray,
    entries: [],
  };
  const noteField = previousFundingsSection?.fields?.find((f: SectionFieldDTO) => f.fieldName === NOTE_FIELD_NAME);
  const noteBookmarked = Boolean(noteField?.addToActionItems);

  // Adjustment rows from earlier reports stay in the table, so the comment follows the latest one only —
  // an older negative row must not pin the comment to the negative case after the current row turns positive.
  const adjustmentRows = (drawSummary?.previousFundings || [])
    .filter((funding: ProjectPreviousFundingDTO) => funding.isDiscrepancy)
    .sort((a: ProjectPreviousFundingDTO, b: ProjectPreviousFundingDTO) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  const latestAdjustmentRow: ProjectPreviousFundingDTO | undefined = adjustmentRows[adjustmentRows.length - 1];
  // Present even once resolved, so totals matching doesn't fall back to plain AUTO_COMMENT.
  const hasAnyAdjustmentRow = adjustmentRows.length > 0;
  const latestAdjustmentAmount = latestAdjustmentRow?.receivedByContractor ?? 0;
  const hasNegativeAdjustment = latestAdjustmentAmount < 0;
  const hasPositiveAdjustment = latestAdjustmentAmount > 0;
  // Keys off the entered value, not a manual toggle — resolved once amounts match.
  const latestAdjustmentResolved = Math.abs((latestAdjustmentRow?.amountFunded ?? 0) - latestAdjustmentAmount) < 0.01;
  const discrepancyCommentVariant = getDiscrepancyCommentVariant(
    hasNegativeAdjustment,
    hasNegativeAdjustment && latestAdjustmentResolved,
    hasPositiveAdjustment,
    hasPositiveAdjustment && latestAdjustmentResolved,
  );
  const difference = previousFundingsTotal - fundedByLenderTotal;
  const match = !hasAnyAdjustmentRow && Math.abs(difference) < 0.01;

  useEffect(() => {
    const existingComment = reportState.previousFundingsComment || '';
    if (existingComment.trim().length > 0 && existingComment !== AUTO_COMMENT) {
      savedCustomComment.current = existingComment;
    }
  }, []);

  useEffect(() => {
    const matchStateChanged = previousMatchState.current !== null && previousMatchState.current !== match;
    const isInitialLoad = previousMatchState.current === null;
    // Regenerate the comment on a variant change too, not just a match/mismatch flip.
    const variantStateChanged =
      previousDiscrepancyCommentVariant.current !== null && previousDiscrepancyCommentVariant.current !== discrepancyCommentVariant;

    if (match) {
      if (matchStateChanged) {
        if (!isInitialLoad) {
          setIsDirty(true);
        }
        setReportState((prev: any) => ({
          ...prev,
          previousFundingsComment: AUTO_COMMENT,
        }));
        savedCustomComment.current = null;
      } else if (isInitialLoad) {
        const existingComment = reportState.previousFundingsComment || '';
        if (!existingComment.trim()) {
          setReportState((prev: any) => ({
            ...prev,
            previousFundingsComment: AUTO_COMMENT,
          }));
        } else if (existingComment !== AUTO_COMMENT) {
          savedCustomComment.current = existingComment;
        }
      }
      lastAutoComment.current = null;
      setShowMismatchAlert(false);
    } else {
      const nextAutoComment = buildDiscrepancyAutoComment(recommendedTotal, previousFundingsTotal, discrepancyCommentVariant);
      const existingComment = reportState.previousFundingsComment || '';
      // Unedited = blank, the auto-comment we last wrote, or any variant's auto-comment for the current totals.
      const isUneditedAutoComment =
        !existingComment.trim() ||
        existingComment === lastAutoComment.current ||
        ALL_DISCREPANCY_COMMENT_VARIANTS.some(
          variant => existingComment === buildDiscrepancyAutoComment(recommendedTotal, previousFundingsTotal, variant),
        );

      if (matchStateChanged || (!isInitialLoad && variantStateChanged)) {
        setIsDirty(true);
        setReportState((prev: any) => ({
          ...prev,
          previousFundingsComment: nextAutoComment,
        }));
        savedCustomComment.current = null;
      } else if (isUneditedAutoComment) {
        // Keep an unedited auto-comment in step with the table, so its amounts match the alert.
        // On load this regenerates for the current variant, same as the "Restore auto-comment" button.
        if (existingComment !== nextAutoComment) {
          if (!isInitialLoad) {
            setIsDirty(true);
          }
          setReportState((prev: any) => ({
            ...prev,
            previousFundingsComment: nextAutoComment,
          }));
        }
      } else if (isInitialLoad) {
        savedCustomComment.current = existingComment;
      }
      lastAutoComment.current = nextAutoComment;
      // Hide the warning once the latest adjustment row is resolved; the comment keeps the resolved wording.
      setShowMismatchAlert(!(hasAnyAdjustmentRow && latestAdjustmentResolved));
    }

    previousMatchState.current = match;
    previousDiscrepancyCommentVariant.current = discrepancyCommentVariant;
  }, [
    previousFundingsTotal,
    fundedByLenderTotal,
    recommendedTotal,
    hasAnyAdjustmentRow,
    latestAdjustmentResolved,
    discrepancyCommentVariant,
  ]);

  /**
   * Writes an edit to the PRIMARY payapp's draw summary.
   *
   * Writes `draws` only — `drawSummary` is derived from it by the context, so writing the mirror
   * directly would have no effect. This section is deliberately scoped to the primary payapp for
   * now: it reads and writes only that draw's previous fundings.
   */
  const handleDrawEditChange = (field: string, value: any) => {
    setIsDirty(true);
    setReportState((prev: any) => {
      const prevDraws: any[] = prev.draws?.length
        ? prev.draws
        : prev.drawSummary
          ? [{ drawId: prev.drawSummary.id, drawSummary: prev.drawSummary, budgetSummary: prev.budgetSummary, lineItems: prev.lineItems }]
          : [];

      // The primary payapp is the last entry (highest application number).
      const primaryDrawId = prevDraws.length > 0 ? prevDraws[prevDraws.length - 1].drawId : undefined;
      if (!primaryDrawId) {
        // No draw list at all (shouldn't happen once a report is open) — nothing to write.
        return prev;
      }

      const nextDraws = prevDraws.map(draw =>
        draw.drawId === primaryDrawId ? { ...draw, drawSummary: { ...draw.drawSummary, [field]: value } } : draw,
      );

      return { ...prev, draws: nextDraws };
    });
  };

  const handleCommentChange = (newComment: string) => {
    setIsDirty(true);
    setReportState((prev: any) => ({
      ...prev,
      previousFundingsComment: newComment,
    }));
    if (newComment.trim() && newComment !== AUTO_COMMENT) {
      savedCustomComment.current = newComment;
    } else if (newComment === AUTO_COMMENT || !newComment.trim()) {
      savedCustomComment.current = null;
    }
  };

  const handleRefreshComment = () => {
    setIsDirty(true);
    if (match) {
      setReportState((prev: any) => ({
        ...prev,
        previousFundingsComment: AUTO_COMMENT,
      }));
      savedCustomComment.current = null;
    } else {
      const nextAutoComment = buildDiscrepancyAutoComment(recommendedTotal, previousFundingsTotal, discrepancyCommentVariant);
      setReportState((prev: any) => ({
        ...prev,
        previousFundingsComment: nextAutoComment,
      }));
      lastAutoComment.current = nextAutoComment;
      savedCustomComment.current = null;
    }
  };

  const handlePreviousFundingsSectionCommentsChange = (updatedCommentsField: SectionFieldDTO) => {
    setIsDirty(true);
    setReportState((prev: any) => {
      const stateSections = (prev.sections || {}) as any;
      const sectionKey = PREVIOUS_FUNDINGS_SECTION_KEY;
      const currentSection = stateSections[sectionKey] ||
        stateSections.previousFundings ||
        stateSections.previousFunding ||
        stateSections.previousFundingsSection || { fields: [] };

      const otherFields = (currentSection.fields || []).filter(
        (field: SectionFieldDTO) => field.fieldName !== updatedCommentsField.fieldName,
      );
      const updatedSection: SectionFieldCollection = {
        ...currentSection,
        fields: [...otherFields, updatedCommentsField],
      };

      return {
        ...prev,
        sections: {
          ...stateSections,
          [sectionKey]: updatedSection,
        },
      };
    });
  };

  const handleToggleNoteBookmark = () => {
    setIsDirty(true);
    setReportState((prev: any) => {
      const stateSections = (prev.sections || {}) as any;
      const sectionKey = PREVIOUS_FUNDINGS_SECTION_KEY;
      const currentSection = stateSections[sectionKey] ||
        stateSections.previousFundings ||
        stateSections.previousFunding ||
        stateSections.previousFundingsSection || { fields: [] };

      const currentNoteField = currentSection.fields?.find((f: SectionFieldDTO) => f.fieldName === NOTE_FIELD_NAME);
      const nextAddToActionItems = !currentNoteField?.addToActionItems;
      const nextActionItemOrder = nextAddToActionItems
        ? (getNextActionItemOrder(stateSections) ?? currentNoteField?.actionItemOrder)
        : undefined;

      // Value is intentionally not stored here — the live comment (reportState.previousFundingsComment)
      // is always the source of truth and is passed separately into buildMagicActionItemsHtml, so this
      // field only tracks bookmark state and avoids getting out of sync when the comment is edited.
      const otherFields = (currentSection.fields || []).filter((f: SectionFieldDTO) => f.fieldName !== NOTE_FIELD_NAME);
      const updatedNoteField: SectionFieldDTO = {
        fieldName: NOTE_FIELD_NAME,
        fieldType: FieldType.Text,
        ...currentNoteField,
        addToActionItems: nextAddToActionItems,
        actionItemOrder: nextActionItemOrder,
      };

      const { previousFundings, previousFunding, previousFundingsSection, ...restSections } = stateSections;

      const updatedSection: SectionFieldCollection = {
        ...currentSection,
        fields: [...otherFields, updatedNoteField],
      };

      return {
        ...prev,
        sections: {
          ...restSections,
          [sectionKey]: updatedSection,
        },
      };
    });
  };

  const handleToggleCompleted = useCallback(() => {
    setIsDirty(true);
    setReportState((prev: any) => {
      const stateSections = (prev.sections || {}) as any;
      const sectionKey = PREVIOUS_FUNDINGS_SECTION_KEY;
      const currentSection = stateSections[sectionKey] ||
        stateSections.previousFundings ||
        stateSections.previousFunding ||
        stateSections.previousFundingsSection || { fields: [] };
      const updatedSection: SectionFieldCollection = { ...currentSection, isCompleted: !currentSection.isCompleted };

      const { previousFundings, previousFunding, previousFundingsSection, ...restSections } = stateSections;

      return {
        ...prev,
        sections: {
          ...restSections,
          [sectionKey]: updatedSection,
        },
      };
    });
  }, [setReportState, setIsDirty]);

  const { isSavingCompletion } = useCompletionToggleEffect({
    isCompleted,
    onToggle: nextCompleted => setExpanded(!nextCompleted),
    saveReport,
  });

  const completeToggle = <SectionCompleteToggle isCompleted={isCompleted} onToggle={handleToggleCompleted} isSaving={isSavingCompletion} />;

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{ px: 2, '&.MuiAccordion-root': { borderRadius: '10px' }, '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {completeToggle}
          <Typography variant='h5' color='primary' sx={{ lineHeight: 1.5, opacity: isCompleted ? 0.6 : 1 }}>
            Previous Fundings
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, pb: 2, position: 'relative' }}>
        <Box aria-hidden={isCompleted} {...(isCompleted ? { inert: '' } : {})} sx={isCompleted ? { opacity: 0.6 } : undefined}>
          <ProjectPreviousFundingsTable
            previousFundings={drawSummary?.previousFundings}
            onChange={(updated: ProjectPreviousFundingDTO[]) => handleDrawEditChange('previousFundings', updated)}
            isMagicReport={true}
            isDirty={isDirty}
          />

          <Box sx={{ mt: 1 }}>
            <Typography variant='caption' sx={{ fontStyle: 'italic', color: 'text.secondary', m: 0, lineHeight: 1.2, display: 'block' }}>
              * Not reviewed by CDCC
            </Typography>
            <Typography variant='caption' sx={{ fontStyle: 'italic', color: 'text.secondary', m: 0, lineHeight: 1.2, display: 'block' }}>
              Click Application # to indicate that a draw was unreviewed
            </Typography>
          </Box>

          {showMismatchAlert && (
            <Alert severity='warning' sx={{ mt: 2, py: 0.5 }}>
              <Typography variant='body2' fontWeight='bold' sx={{ m: 0, fontSize: '0.8125rem', lineHeight: 1.3 }}>
                Discrepancy between expected and payapp Previous Funding amount. Enter comment.
              </Typography>
              <Typography variant='body2' sx={{ m: 0, mt: 0.5, fontSize: '0.8125rem', lineHeight: 1.3 }}>
                <strong>Received by Contractor:</strong> {formatCurrency(previousFundingsTotal.toString())}
              </Typography>
              <Typography variant='body2' sx={{ m: 0, fontSize: '0.8125rem', lineHeight: 1.3 }}>
                <strong>Funded by Lender (per last CDCC recommendation):</strong> {formatCurrency(fundedByLenderTotal.toString())}
              </Typography>
              <Typography variant='body2' sx={{ m: 0, fontSize: '0.8125rem', lineHeight: 1.3 }}>
                <strong>Difference:</strong> {formatCurrency(difference.toString())}
              </Typography>
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label='Note'
                value={comment}
                onChange={e => handleCommentChange(e.target.value)}
                variant='outlined'
                placeholder={match ? AUTO_COMMENT : 'Previous fundings note'}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                  },
                }}
              />
              <IconButton size='small' onClick={handleRefreshComment} sx={{ color: 'primary.main', mt: 1 }} title='Restore auto-comment'>
                <RefreshIcon />
              </IconButton>
              <BookmarkToggle
                addToActionItems={noteBookmarked}
                onToggle={handleToggleNoteBookmark}
                sx={{ mt: 1.5 }}
                ariaLabel='Add note to action items'
              />
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <CommentsSection
              commentsField={previousFundingsSectionCommentsField}
              onChange={handlePreviousFundingsSectionCommentsChange}
              title='Comments'
            />
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default PreviousFundingsSection;
